import axios from 'axios';
import type { IndividualRegistrationData, ProviderRegistrationData, RegistrationResponse } from './types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('careVaultToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - only clear storage, don't force navigate
      // Let the auth context handle navigation properly
      localStorage.removeItem('careVaultToken');
      localStorage.removeItem('careVaultUser');
      // Don't use window.location.href as it causes hard reload and loses context
      // The auth context will handle proper navigation
    }
    return Promise.reject(error);
  }
);

// Auth API - All authentication goes through backend
export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },



  // New specific registration methods
  registerIndividual: async (data: IndividualRegistrationData): Promise<RegistrationResponse> => {
    const response = await api.post('/auth/register/individual', data);
    return response.data;
  },

  registerProvider: async (data: ProviderRegistrationData): Promise<RegistrationResponse> => {
    const response = await api.post('/auth/register/provider', data);
    return response.data;
  },

  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  updateProfile: async (name: string) => {
    const response = await api.put('/auth/profile', { name });
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  resendConfirmation: async (email: string) => {
    const response = await api.post('/auth/resend-confirmation', { email });
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  getVerificationStatus: async () => {
    const response = await api.get('/auth/verification-status');
    return response.data;
  },
};

// Records API
export const recordsAPI = {
  getRecords: async (params?: {
    type?: 'personal' | 'medical';
    search?: string;
    limit?: number;
    offset?: number;
  }) => {
    const response = await api.get('/records', { params });
    return response.data;
  },

  getRecord: async (id: string) => {
    const response = await api.get(`/records/${id}`);
    return response.data;
  },

  createPersonalLog: async (data: {
    condition?: string;
    notes?: string;
    timestamp?: string;
  }) => {
    const response = await api.post('/records/personal', data);
    return response.data;
  },

  createMedicalRecord: async (data: {
    documentType: string;
    recordDetails?: string;
    timestamp?: string;
  }, file?: File) => {
    const formData = new FormData();

    // Add form fields
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined) {
        formData.append(key, value);
      }
    });

    // Add file if present
    if (file) {
      formData.append('document', file);
    }

    const response = await api.post('/records/medical', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  updateRecord: async (id: string, data: any) => {
    const response = await api.put(`/records/${id}`, data);
    return response.data;
  },

  deleteRecord: async (id: string) => {
    const response = await api.delete(`/records/${id}`);
    return response.data;
  },

  getDashboardStats: async () => {
    const response = await api.get('/records/dashboard/stats');
    return response.data;
  },
};

// Subscription API
export const subscriptionAPI = {
  verify: async (reference: string, plan: string) => {
    const response = await api.post('/subscriptions/verify', { reference, plan });
    return response.data;
  },

  getPlans: async () => {
    const response = await api.get('/subscriptions/plans');
    return response.data;
  }
};

// Provider API - For healthcare providers accessing patient-shared data
export const providerAPI = {
  getDashboardStats: async () => {
    const response = await api.get('/provider-patients/stats');
    return response.data;
  },

  getPatients: async (params?: { search?: string; limit?: number; offset?: number }) => {
    const response = await api.get('/provider-patients', { params });
    return response.data;
  },

  getPatient: async (id: string) => {
    const response = await api.get(`/provider-patients/${id}`);
    return response.data;
  },

  createPatient: async (data: {
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    dateOfBirth?: string;
  }) => {
    const response = await api.post('/provider-patients', data);
    return response.data;
  },

  searchPatients: async (params?: { q?: string; dob?: string; mrn?: string; limit?: number }) => {
    const response = await api.get('/provider-patients/search', { params });
    return response.data;
  },

  getSharedRecords: async (params?: { patientId?: string; limit?: number; offset?: number }) => {
    const response = await api.get('/provider-records/shared', { params });
    return response.data;
  },

  getAnalytics: async (period?: string) => {
    const response = await api.get('/provider-access/stats', { params: { period } });
    return response.data;
  },

  getRecordTypes: async () => {
    const response = await api.get('/provider-records/types');
    return response.data;
  },

  createPatientRecord: async (data: {
    patientId: string;
    recordType: string;
    title: string;
    content?: Record<string, any>;
    categoryId?: string;
  }) => {
    const response = await api.post('/provider-records', data);
    return response.data;
  },

  getPatientRecords: async (patientId: string, params?: { limit?: number; offset?: number }) => {
    const response = await api.get('/provider-records', { params: { patientId, ...params } });
    return response.data;
  },
};

// Provider Access API - For provider registration, staff management, and access control
export const providerAccessAPI = {
  // Provider Registration
  registerProvider: async (data: {
    institutionName: string;
    institutionType: string;
    licenseNumber?: string;
    npiNumber?: string;
    taxId?: string;
    address?: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
    };
    phone?: string;
    website?: string;
  }) => {
    const response = await api.post('/provider-access/register', data);
    return response.data;
  },

  getProviderProfile: async () => {
    const response = await api.get('/provider-access/profile');
    return response.data;
  },

  getProviderStats: async (params?: { startDate?: string; endDate?: string }) => {
    const response = await api.get('/provider-access/stats', { params });
    return response.data;
  },

  // Staff Management
  addStaffMember: async (data: {
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    department?: string;
    phone?: string;
    isAdmin?: boolean;
  }) => {
    const response = await api.post('/provider-access/staff', data);
    return response.data;
  },

  getStaffMembers: async () => {
    const response = await api.get('/provider-access/staff');
    return response.data;
  },

  getStaffStats: async (staffId?: string) => {
    const response = await api.get('/provider-access/staff/stats', { params: { staffId } });
    return response.data;
  },

  deactivateStaffMember: async (staffId: string) => {
    const response = await api.delete(`/provider-access/staff/${staffId}`);
    return response.data;
  },

  // Patient Access Code (for patients)
  generateAccessCode: async (data: {
    duration: '15min' | '1hour' | '24hours' | '7days' | '30days' | 'permanent';
    categories?: string[];
  }) => {
    const response = await api.post('/provider-access/access-code', data);
    return response.data;
  },

  // Active Sessions (for patients)
  getActiveSessions: async () => {
    const response = await api.get('/provider-access/sessions/active');
    return response.data;
  },

  revokeSession: async (sessionId: string, reason?: string) => {
    const response = await api.post(`/provider-access/sessions/${sessionId}/revoke`, { reason });
    return response.data;
  },

  // Audit Log (for patients)
  getAuditLog: async (params?: { startDate?: string; endDate?: string; limit?: number }) => {
    const response = await api.get('/provider-access/audit-log', { params });
    return response.data;
  },
};

// Announcements API - For user-facing announcement interactions
export const announcementsAPI = {
  /**
   * Get active announcements for the current user
   * Returns announcements filtered by status, date, targeting, and frequency limits
   * Sorted by priority (critical > high > medium > low)
   */
  getActiveForUser: async () => {
    const response = await api.get('/announcements/user/active');
    return response.data;
  },

  /**
   * Record a view of an announcement
   * @param announcementId - The announcement ID
   * @param dismissed - Whether the user dismissed the announcement
   */
  recordView: async (announcementId: string, dismissed: boolean = false) => {
    const response = await api.post(`/announcements/user/${announcementId}/view`, { dismissed });
    return response.data;
  },

  /**
   * Record an engagement event (button click, link click)
   * @param announcementId - The announcement ID
   * @param type - Type of engagement ('button_click' | 'link_click')
   * @param data - Additional engagement data (e.g., button_id, url)
   */
  recordEngagement: async (
    announcementId: string,
    type: 'button_click' | 'link_click',
    data?: { button_id?: string; url?: string }
  ) => {
    const response = await api.post(`/announcements/user/${announcementId}/engagement`, { type, data });
    return response.data;
  },

  /**
   * Submit a poll response
   * @param announcementId - The announcement ID
   * @param optionId - The selected poll option ID
   */
  submitPollResponse: async (announcementId: string, optionId: string) => {
    const response = await api.post(`/announcements/user/${announcementId}/poll`, { optionId });
    return response.data;
  },

  /**
   * Get poll results for an announcement
   * @param announcementId - The announcement ID
   */
  getPollResults: async (announcementId: string) => {
    const response = await api.get(`/announcements/user/${announcementId}/poll`);
    return response.data;
  },
};

// Health check
export const healthCheck = async () => {
  const response = await api.get('/health');
  return response.data;
};

export default api;